import React, { useEffect, useState, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { can, canManageEmployees, P } from "@/lib/perms";
import { EmployeeWelcome } from "@/components/org/EmployeeWelcome";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { playNotificationSound, unlockAudio } from "@/lib/sound";
import { SecureImage } from "@/components/SecureImage";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, Package, Users, Handshake, ClipboardList, Bell, Settings,
  UserCircle, LogOut, Store, Menu, Wallet, Boxes, Building2,
} from "lucide-react";

const NOTIFICATION_POLL_INTERVAL_MS = 20000;

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard", id: "dashboard", perms: P.dashboard },
  { to: "/app/products", icon: Package, label: "Products", id: "products", perms: P.catalog },
  { to: "/app/organization", icon: Building2, label: "Organisation", id: "organization", show: canManageEmployees },
  { to: "/app/partners", icon: Handshake, label: "Partners", id: "partners", perms: P.partners },
  { to: "/app/orders", icon: ClipboardList, label: "Orders", id: "orders", perms: P.orders },
  { to: "/app/inventory", icon: Boxes, label: "Inventory", id: "inventory", perms: P.inventory },
  { to: "/app/reports", icon: Wallet, label: "Reports", id: "reports", perms: P.reports },
  { to: "/app/notifications", icon: Bell, label: "Notifications", id: "notifications" },
  { to: "/app/settings", icon: Settings, label: "Settings", id: "settings" },
  { to: "/app/profile", icon: UserCircle, label: "Profile", id: "profile" },
];

export default function DashboardLayout() {
  const { user, business, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = NAV.filter((n) => (n.show ? n.show(user) : !n.perms || can(user, ...n.perms)));

  useEffect(() => {
    const status = business?.org_setup?.status || "pending";
    if (user?.role !== "owner" || status !== "pending" || location.pathname === "/app/setup") return;
    if (sessionStorage.getItem("rv_setup_prompted")) return;
    sessionStorage.setItem("rv_setup_prompted", "1");
    navigate("/app/setup");
  }, [user, business, location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === "/app" && user && !can(user, ...P.dashboard) && nav.length) navigate(nav[0].to, { replace: true });
  }, [user, location.pathname, nav, navigate]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(null);
  const typeLabel = BUSINESS_TYPE_LABELS[business?.business_type] || business?.business_type;

  useEffect(() => {
    const check = (r) => {
      const n = r.data.unread;
      if (prevUnread.current !== null && n > prevUnread.current) playNotificationSound();
      prevUnread.current = n;
      setUnread(n);
    };
    api.get("/notifications").then(check).catch(() => {});
    const t = setInterval(() => api.get("/notifications").then(check).catch(() => {}), NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Unlock Web Audio on the first user interaction so poll-triggered chimes can play
  // (browsers block audio that doesn't originate from a user gesture).
  useEffect(() => {
    const unlock = () => {
      unlockAudio();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  const RoleIcon = Store;
  const accent = "#0D5C3A";

  const Sidebar = (
    <aside className="flex h-full w-[260px] flex-col border-r border-[#E5E0D8] bg-white">
      <div className="flex items-center gap-2 px-6 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0D5C3A] text-white"><Store className="h-5 w-5" /></div>
        <span className="font-heading text-lg font-extrabold tracking-tight text-[#0D5C3A]">RESTRO<span className="text-[#C85A32]">-VENDS</span></span>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {nav.map((n) => (
          <NavLink key={n.id} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.id}`}
            className={({ isActive }) => `relative flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#F0ECE1] text-[#0D5C3A]" : "text-stone-600 hover:bg-[#FAF8F5]"}`}>
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 h-6 w-1 rounded-r bg-[#0D5C3A]" />}
                <n.icon className="h-5 w-5" />
                <span>{n.label}</span>
                {n.id === "notifications" && unread > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#C85A32] px-1.5 text-xs font-bold text-white">{unread}</span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-2xl border border-[#E5E0D8] bg-[#FAF8F5] p-4">
        <div className="flex items-center gap-3">
          <SecureImage fileId={business?.logo_file_id} className="h-10 w-10 rounded-xl object-cover" />
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-semibold text-stone-900">{business?.name}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: accent }}>
              <RoleIcon className="h-3 w-3" /> {typeLabel}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAF8F5]">
      <EmployeeWelcome />
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-40 flex items-center justify-between gap-3 border-b border-[#E5E0D8] bg-[#FAF8F5]/80 px-4 py-3 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
            <div className="hidden items-center gap-2 sm:flex">
              <SecureImage fileId={business?.logo_file_id} className="h-9 w-9 rounded-lg object-cover" />
              <div>
                <p className="font-heading text-sm font-bold leading-tight text-stone-900">{business?.name}</p>
                <p className="text-xs capitalize text-stone-500" data-testid="topbar-user-role">{typeLabel} · {user?.role === "owner" ? "Super Admin" : user?.job_role || "Employee"}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ProfileCodeChip code={business?.profile_code} />
            <button data-testid="topbar-profile-btn" onClick={() => navigate("/app/profile")} className="rounded-lg p-2 text-stone-600 hover:bg-white" title="View Profile"><UserCircle className="h-5 w-5" /></button>
            <button data-testid="topbar-signout-btn" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </header>
        <main className="rv-scroll flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
